import { SearchRegistry } from '../types';

export const userManagementSearchEntries: SearchRegistry = {
  '/users/profile': {
    keywords: [
      'my profile',
      'profile',
      'user profile',
      'personal info',
      'account profile',
      'user details',
      'profil',
      'mein profil',
      'profile page',
      'change avatar',
      'profile picture',
      'change password',
      'time zone',
      'language preference',
    ],
    questions: [
      'Where do I see my own profile?',
      'How do I update my personal information?',
      'How do I change my profile picture?',
      'How do I change my display language?',
    ],
  },
  '/users/edit': {
    keywords: [
      'edit user',
      'update user',
      'modify user',
      'change user',
      'user edit',
      'edit profile',
      'change role',
      'change roles',
      'edit roles',
      'reassign user',
    ],
    questions: [
      "How do I edit a user's profile?",
      "Where do I update a user's details as an admin?",
      'How do I change which roles a user has?',
    ],
  },
  '/users/list': {
    keywords: [
      'all users',
      'user list',
      'users overview',
      'user directory',
      'list users',
      'partner users',
      'team members',
      'benutzerliste',
      'user table',
      'filter users',
      'search users',
      'user roles column',
      'user status column',
      'last login column',
    ],
    questions: [
      'Where do I see all users for my partner?',
      'How do I list users in the portal?',
      'How do I search for a user by email?',
      'How do I see when a user last logged in?',
    ],
    faqs: [
      {
        q: 'Where do I find all users belonging to my partner?',
        a: 'Open User Management → Users → All Users. The list is filtered to your partner; admins can search, filter by role and open user detail pages.',
      },
    ],
  },
  '/users/create': {
    keywords: [
      'add user',
      'create user',
      'new user',
      'invite user',
      'register user',
      'add team member',
      'benutzer anlegen',
      'invite colleague',
      'invitation email',
      'activation link',
      'pick roles',
    ],
    questions: [
      'How do I add a new user?',
      'How do I invite a colleague to the portal?',
      'Which roles can I assign to a new user?',
      'How does the activation email work?',
    ],
    faqs: [
      {
        q: 'How do I invite a new user to the portal?',
        a: 'Open User Management → Users → Add User. Enter the email and pick the roles. The new user receives an invitation email with the activation link.',
      },
    ],
  },
  '/users/guide': {
    keywords: [
      'user management guide',
      'user guide',
      'role guide',
      'permissions guide',
      'roles documentation',
      'user and role management guide',
      'partner types',
      'roles',
      'user groups',
      'access control',
      'access guide',
      'rbac',
      'every user must have',
      'one user group',
    ],
    questions: [
      'Where is the internal user-management guide?',
      'Which roles exist and what do they grant?',
      'What is a Partner Type, a Role and a User Group in the portal?',
    ],
    faqs: [
      {
        q: 'How does access control work in the ryd portal?',
        a: 'Access is controlled by combining three concepts: Partner Types, Roles and User Groups. Every user must have at least one Partner Type and one Role, and belongs to exactly one User Group (a partner). The User & Role Management Guide explains every category and how they combine to gate pages and actions.',
      },
    ],
  },
  '/profile/security': {
    keywords: [
      'security settings',
      'security and 2fa',
      '2fa',
      'two factor authentication',
      'multi-factor authentication',
      'mfa',
      'authenticator app',
      'totp',
      'email otp',
      'backup codes',
      'disable 2fa',
      'security page',
      'account security',
      'passkey',
    ],
    questions: [
      'Where do I manage my 2FA settings?',
      'How do I enable authenticator-app login protection?',
      'How do I switch from email OTP to an authenticator app?',
      'Where do I see my backup codes and current 2FA status?',
    ],
    faqs: [
      {
        q: 'How do I enable 2FA on my portal account?',
        a: 'Open User Management → Users → Security & 2FA. The page shows your current status and lets you enable either Email OTP or an authenticator-app (TOTP) flow, then manage backup codes from the same screen.',
      },
    ],
  },
  '/partner-administration/list': {
    keywords: [
      'all partners',
      'partner list',
      'partner directory',
      'partners overview',
      'list of partners',
      'registered partners',
      'partner administration',
      'partner table',
      'filter partners',
      'partner type filter',
      'partner status',
    ],
    questions: [
      'Where do I see all registered partners?',
      'How do I search for a partner by name?',
      'How do I filter partners by type?',
    ],
  },
  '/partner-administration/create': {
    keywords: [
      'add partner',
      'create partner',
      'new partner',
      'register partner',
      'partner onboarding',
      'partner setup',
      'add tenant',
      'neuen partner anlegen',
      'partner type',
      'partner contact details',
      'partner feature flags',
      'partner provisioning',
    ],
    questions: [
      'How do I add a new partner to the portal?',
      'Where do I onboard a new API or fleet partner?',
      'Which partner types can I create?',
    ],
    faqs: [
      {
        q: 'How do I create a new partner?',
        a: 'Open User Management → Partner Administration → Add Partner. Fill in the partner type, contact details and feature flags, then save. The partner is provisioned and an admin user is invited via email.',
      },
    ],
  },
  '/partner-administration/ui-settings/list': {
    keywords: [
      'ui settings list',
      'all ui settings',
      'ui settings overview',
      'partner ui settings',
      'theme list',
      'theme directory',
    ],
    questions: ['Where do I see all UI settings across partners?'],
  },
  '/partner-administration/ui-settings/create': {
    keywords: [
      'create ui settings',
      'new ui settings',
      'add theme',
      'configure partner theme',
      'create theme',
      'partner theme creation',
    ],
    questions: [
      'How do I create UI settings for a partner?',
      'Where do I configure a new theme for a partner?',
    ],
  },
  '/partner-administration/ui-settings/edit': {
    keywords: [
      'edit ui settings',
      'update theme',
      'modify ui settings',
      'change partner theme',
      'update partner branding',
      'partner theme update',
    ],
    questions: ['How do I update an existing partner UI settings configuration?'],
  },
  '/partner-administration/partner-integration-kb': {
    keywords: [
      'partner integration knowledge base',
      'integration kb',
      'partner kb',
      'integration knowledge',
      'integration notes',
      'integration documentation',
      'partner faq',
      'kb categories',
      'kb search',
      'common partner questions',
    ],
    questions: [
      'Where is the partner integration knowledge base?',
      'How do I find integration notes for a specific partner?',
      'How do I search the integration KB?',
    ],
    faqs: [
      {
        q: 'How is the Partner Integration Knowledge Base organised?',
        a: 'The KB groups partner-integration content into categories shown on the right of the page. Use the search box to find a question across all categories or click a category to jump to its section; each entry expands to a curated answer with screenshots and links.',
      },
    ],
  },
  '/partner-administration/key-account-managers': {
    keywords: [
      'key account managers',
      'kam',
      'account manager',
      'partner kam',
      'partner owner',
      'partner contact',
      'kam assignment',
      'kam directory',
    ],
    questions: [
      'Who is the key account manager for partner X?',
      'Where do I see partner KAM assignments?',
      'How do I reassign a partner to a different KAM?',
    ],
  },
  '/partner-administration/alert-slack-channels': {
    keywords: [
      'alert slack channels',
      'partner slack',
      'slack channel',
      'partner alerts slack',
      'platform communications admin',
      'alert routing partner',
      'slack webhook',
      'slack incoming webhook',
      'channel mapping',
    ],
    questions: [
      'How do I configure the Slack channels that receive partner alerts?',
      'Where do I attach a Slack channel to a partner?',
      'How do I test that a Slack channel mapping works?',
    ],
  },
};
